// src/stage1/VideoSky.jsx
import * as THREE from 'three';
import { useEffect, useRef } from 'react';
import { useVideoTexture } from '@react-three/drei';

export default function VideoSky({ src, playing = true, onEnded }) {
  const texture = useVideoTexture(src, {
    loop: false,
    muted: false,
    crossOrigin: 'anonymous',
    autoplay: false, // we start it manually
  });

  const videoRef = useRef(null);

  useEffect(() => {
    if (!texture?.image) return;
    const video = texture.image;
    videoRef.current = video;

    video.muted = false;
    video.playsInline = true;
    video.setAttribute('webkit-playsinline', 'true');

    const handleEnded = () => onEnded?.();
    video.addEventListener('ended', handleEnded);

    // ⬇️ Start playback (autoplay-safe)
    if (playing) {
      video.play().catch(() => {
        const listener = () => { video.play().catch(() => {}); window.removeEventListener('click', listener); };
        window.addEventListener('click', listener);
      });
    } else {
      video.pause();
      video.currentTime = 0;
    }

    return () => {
      video.removeEventListener('ended', handleEnded);
    };
  }, [texture, playing, onEnded]);

  // ✅ Correct color space
  if (texture) {
    texture.colorSpace = THREE.SRGBColorSpace;

    // ✅ Flip horizontally so it isn't mirrored when viewed on BackSide
    texture.wrapS = THREE.RepeatWrapping;
    texture.repeat.x = -1;
    texture.offset.x = 1;
  }

  return (
    <mesh>{/* 180° yaw often matches drei's Environment */}
      <sphereGeometry args={[50, 64, 32]} />
      <meshBasicMaterial
        map={texture}
        side={THREE.BackSide}
        toneMapped={false}
      />
    </mesh>
  );
}
